import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ExternalLink, MapPin, MessageSquare, Search, Send, X } from 'lucide-react';
import {
  fetchOrderDetails, transformOrderDetails, transformOrderFromNeon, fetchBcProductImages,
  fetchUpsTracking, fetchVipRecord, fetchBcStoreCredit, fetchOrderNotes, addOrderNote,
  fetchOrderHistory, fetchOrderDesk, addOrderDeskEntry, resolveOrderDeskProblem,
  fetchPreviousShipAddresses, fetchOrderTakenBy, fetchOrderAttribution, sendCustomerSms,
} from '../services/api';
import { getCurrentUser } from '../services/auth';
import { toTitleCase } from '../utils/format';
import VipRewardsModal from '../components/VipRewardsModal';
import ListStatus from '../components/ListStatus';

// Order Desk (beta). The order screen from the approved Order Desk mockup (v2, Rachael's review,
// Oct 29, 2026), on real orders, so Rachael and Ashley can work it and say what to change.
// Greg, Oct 29, 2026: "create a new order details, stick it in the nav and let her play with it
// live there... on the actual data."
//
// ⚠️ SAME DATA AS ORDER DETAILS, NOT A COPY OF IT. Every read here is a call OrderDetails.js already
// makes (fetchOrderDetails + transformOrderFromNeon, notes, history, VIP, store credit, UPS,
// order-taken-by). Internal notes are the SAME office_notes_log notes through order-note-add, so
// a note written here shows on the classic page and the old admin. The only new storage is
// lib/order-desk.js: Customer relations entries, Problems & resolutions, and a record of texts
// sent from here.
//
// ⚠️ NO ORDER EDITS FROM HERE YET. Addresses, items, holds and cancels stay on the classic page
// ("Classic view" in the header). "Use this address" from the mockup is not built: it is an order
// edit, and Rachael has not said yet whether it should touch the label, BigCommerce or both.

// BigCommerce control panel for crabplace.com (same constant as OrderDetails.js).
const BC_CONTROL_PANEL = 'https://store-3mvkyi71x6.mybigcommerce.com';

const TABS = [
  ['items', 'Order items'],
  ['payment', 'Payment'],
  ['activity', 'Activity'],
  ['history', 'History'],
  ['cr', 'Customer relations'],
  ['details', 'Order details'],
];

// ── Dates ────────────────────────────────────────────────────────────────────
// Neon order dates are text ('2026-10-20 12:10:00.000', no zone, Eastern by convention).
// Swap the space for a T so every browser parses it, as local time.
function when(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v).trim();
  const d = new Date(/^\d{4}-\d{2}-\d{2} \d/.test(s) ? s.replace(' ', 'T') : s);
  return isNaN(d) ? null : d;
}
const isMidnight = (d) => d && d.getHours() === 0 && d.getMinutes() === 0;
const fmtDay = (d) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtWeekday = (d) => (d ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '');
const fmtTime = (d) => (d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
// "Oct 20, 2026 · 12:10 PM", or just the day when the source only has a date.
const fmtStamp = (d) => (!d ? '' : isMidnight(d) ? fmtDay(d) : `${fmtDay(d)} · ${fmtTime(d)}`);

// UPS gives 'YYYYMMDD' and 'HHMMSS'.
function upsWhen(d, t) {
  if (!d || d.length !== 8) return null;
  const hh = t && t.length >= 4 ? `${t.slice(0, 2)}:${t.slice(2, 4)}` : '00:00';
  return when(`${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}T${hh}:00`);
}

function fmtPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '');
  const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
}
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const plainName = (s) => String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
// Same grouping key the server uses for previous addresses: letters and digits, + ZIP5.
const addrKey = (street, zip) => `${String(street || '').toLowerCase().replace(/[^a-z0-9]/g, '')}|${String(zip || '').trim().slice(0, 5)}`;
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

function OrderLookup({ navigateTo, compact }) {
  const [q, setQ] = useState('');
  const go = () => { if (q.trim()) navigateTo('order-desk', q.trim()); };
  return (
    <div className={compact ? 'odx-lookup odx-lookup-compact' : 'odx-lookup'}>
      <Search size={14} />
      <input
        className="form-input"
        placeholder="Order number, e.g. 2028411"
        value={q}
        inputMode="numeric"
        autoFocus={!compact}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') go(); }}
        aria-label="Open an order by number"
      />
      <button className="btn btn-navy" onClick={go}>Open</button>
    </div>
  );
}

function Label({ children }) { return <div className="odx-label">{children}</div>; }

export default function OrderDesk({ orderId, navigateTo }) {
  const me = getCurrentUser();
  const [order, setOrder] = useState(null);
  const [src, setSrc] = useState(null);          // raw Neon orders row, for the real dates
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState('items');
  const [notesView, setNotesView] = useState('notes'); // 'notes' | 'problems'
  const [vip, setVip] = useState(null);
  const [storeCredit, setStoreCredit] = useState(null);
  const [bcCustomerId, setBcCustomerId] = useState(null);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [internalNotes, setInternalNotes] = useState([]);
  const [systemNotes, setSystemNotes] = useState([]);
  const [ups, setUps] = useState(null);
  const [upsState, setUpsState] = useState('idle'); // idle | loading | done | error
  const [takenBy, setTakenBy] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [desk, setDesk] = useState({ loading: true, exists: false, entries: [], crTypes: [], error: null });
  const [addresses, setAddresses] = useState(null);
  const [showAddresses, setShowAddresses] = useState(false);
  // Forms
  const [newNote, setNewNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [crType, setCrType] = useState('');
  const [crBody, setCrBody] = useState('');
  const [crSaving, setCrSaving] = useState(false);
  const [problemBody, setProblemBody] = useState('');
  const [problemSaving, setProblemSaving] = useState(false);
  const [resolveText, setResolveText] = useState({}); // problem id -> draft resolution
  const [resolving, setResolving] = useState(null);
  const [formMsg, setFormMsg] = useState(null); // { where, ok, text }
  const [showSms, setShowSms] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [now, setNow] = useState(new Date());
  const tabRefs = useRef({});
  const commsRef = useRef(null);

  // The "Stamped automatically" preview next to each save button stays current.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!orderId) return;
    let live = true;
    setOrder(null); setSrc(null); setError(null); setNotFound(false);
    setVip(null); setStoreCredit(null); setBcCustomerId(null); setHistory(null);
    setInternalNotes([]); setSystemNotes([]); setUps(null); setUpsState('idle');
    setTakenBy(null); setAttribution(null); setAddresses(null);
    setDesk({ loading: true, exists: false, entries: [], crTypes: [], error: null });

    fetchOrderDetails(orderId)
      .then(async raw => {
        const o = raw?._fromNeon ? transformOrderFromNeon(raw, null) : transformOrderDetails(raw, null);
        if (!o) throw new Error('Could not read this order.');
        // Product thumbnails from the live BC catalog, keyed on bcProductId (see OrderDetails.js).
        const missing = o.products.filter(p => !p.image);
        if (missing.length) {
          const imgs = await fetchBcProductImages(missing).catch(() => ({}));
          o.products = o.products.map(p => {
            if (p.image) return p;
            const url = (p.bcProductId && imgs[String(p.bcProductId)]) || imgs[`name:${p.name}`];
            return url ? { ...p, image: url } : p;
          });
        }
        if (!live) return;
        setOrder(o);
        setSrc(raw?._fromNeon ? raw.order : null);

        const email = o.customer.username;
        if (email) {
          fetchVipRecord(email).then(v => { if (live) setVip(v); }).catch(() => {});
          fetchBcStoreCredit(email).then(({ credit, bcCustomerId: id }) => {
            if (!live) return;
            setStoreCredit(credit);
            if (id) setBcCustomerId(id);
          }).catch(() => {});
        }
        if (o.internalId) {
          fetchOrderNotes(o.internalId, 1).then(n => { if (live) setInternalNotes(Array.isArray(n) ? n : []); }).catch(() => {});
          fetchOrderNotes(o.internalId, 2).then(n => { if (live) setSystemNotes(Array.isArray(n) ? n : []); }).catch(() => {});
        }
        if (o.customer.id) {
          fetchOrderHistory(o.customer.id).then(h => { if (live) setHistory(h || null); }).catch(() => {});
          fetchPreviousShipAddresses(o.customer.id).then(a => { if (live) setAddresses(Array.isArray(a) ? a : []); })
            .catch(() => { if (live) setAddresses([]); });
        }
        fetchOrderTakenBy(o.id).then(t => { if (live) setTakenBy(t); });
        fetchOrderAttribution(o.id).then(a => { if (live) setAttribution(a); });
        fetchOrderDesk(o.id)
          .then(d => { if (live) setDesk({ loading: false, exists: !!d?.exists, entries: d?.entries || [], crTypes: d?.crTypes || [], error: null }); })
          .catch(e => { if (live) setDesk({ loading: false, exists: false, entries: [], crTypes: [], error: e.message }); });
        if (o.trackingNumber && o.trackingNumber.length > 5) {
          setUpsState('loading');
          fetchUpsTracking(o.trackingNumber)
            .then(d => { if (live) { setUps(d); setUpsState('done'); } })
            .catch(() => { if (live) setUpsState('error'); });
        }
      })
      .catch(e => { if (live) { setError(e.message); setNotFound(!!e.notFound); } });
    return () => { live = false; };
  }, [orderId]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const placed = when(src?.odate);
  const shipDay = when(src?.shipdate);
  const requested = when(src?.delivery_date ?? src?.deliverydate);
  const estimated = when(src?.estimated_delivery_date ?? src?.estimateddeliverydate);
  const delivered = when(src?.actual_delivery_date ?? src?.actualdeliverydatetime);
  const upsEta = ups ? upsWhen(ups.scheduledDelivery) : null;
  // ups-tracking often leaves status blank; the delivered flag and the newest scan say more.
  // actualDelivery is a date only, so take the time from the DELIVERED scan when there is one.
  const upsDeliveredScan = (ups?.activities || []).find(a => /deliver/i.test(a.description || '') && !/out for/i.test(a.description || ''));
  const upsDelivered = ups && (ups.delivered || ups.actualDelivery)
    ? (upsDeliveredScan ? upsWhen(upsDeliveredScan.date, upsDeliveredScan.time) : upsWhen(ups.actualDelivery))
    : null;
  const upsStatus = ups ? (String(ups.status || '').trim()
    || (ups.delivered ? 'Delivered' : String(ups.activities?.[0]?.description || '').trim() || 'In the UPS system')) : '';

  const billing = order?.customer.billing || {};
  const shipping = order?.customer.shipping || {};
  const isGift = !!(order && billing.name && shipping.name
    && billing.name.trim().toLowerCase() !== shipping.name.trim().toLowerCase());
  const customerName = toTitleCase(billing.name || shipping.name || '');
  const histOrders = history?.orders || [];
  // orders_by_customer stops at 50 rows, so 50 means "50 or more".
  const orderCount = history?.summary?.totalNumberOfOrders ?? null;
  const orderCountText = orderCount == null ? '—' : orderCount >= 50 ? '50+' : String(orderCount);
  const currentKey = addrKey(shipping.address, shipping.zip);

  const crEntries = desk.entries.filter(e => e.kind === 'cr');
  const problems = desk.entries.filter(e => e.kind === 'problem')
    .sort((a, b) => (a.status === 'open') !== (b.status === 'open')
      ? (a.status === 'open' ? -1 : 1)
      : new Date(b.created_at) - new Date(a.created_at));
  const openProblems = problems.filter(p => p.status === 'open').length;

  // Activity: everything with a time on it for this order, oldest first (the mockup's "newest last").
  const activity = useMemo(() => {
    if (!order) return [];
    const ev = [];
    const add = (at, title, detail, who, tone) => { if (at) ev.push({ at, title, detail, who, tone }); };
    add(placed, 'Order placed', takenBy ? `Taken by ${takenBy}` : (order.takenBy ? `Taken by ${order.takenBy}` : 'Online'), null);
    add(shipDay, 'Ship date', order.shipMethod || '', null);
    internalNotes.forEach(n => add(when(n.date), 'Internal note', n.description + (n.response ? ` (Response: ${n.response})` : ''), n.employee, 'note'));
    systemNotes.forEach(n => add(when(n.date), 'System note', n.description, n.employee, 'system'));
    (ups?.activities || []).forEach(a => add(upsWhen(a.date, a.time), `UPS: ${a.description || 'scan'}`, a.location || '', null, 'ups'));
    if (!ups?.activities?.length) add(delivered, 'Delivered', '', null, 'ups');
    desk.entries.forEach(e => {
      if (e.kind === 'cr') add(when(e.created_at), `Customer relations: ${e.entry_type}`, e.body, e.created_by, 'cr');
      if (e.kind === 'contact') add(when(e.created_at), `${e.entry_type} sent to customer`, e.body, e.created_by, 'contact');
      if (e.kind === 'problem') {
        add(when(e.created_at), 'Problem logged', e.body, e.created_by, 'problem');
        if (e.resolved_at) add(when(e.resolved_at), 'Problem resolved', e.resolution, e.resolved_by, 'resolved');
      }
    });
    // A date with no time (ship date, a midnight stamp) sorts at the END of its day, or the
    // ship date would land above "Order placed" on a same-day order.
    const key = (d) => (isMidnight(d) ? d.getTime() + 86399000 : d.getTime());
    return ev.sort((a, b) => key(a.at) - key(b.at));
  }, [order, placed, shipDay, delivered, takenBy, internalNotes, systemNotes, ups, desk.entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ────────────────────────────────────────────────────────────────
  const flash = (where, ok, text) => {
    setFormMsg({ where, ok, text });
    setTimeout(() => setFormMsg(m => (m && m.where === where && m.text === text ? null : m)), 5000);
  };

  // Same call and same note type (1 = internal) as Order Details' "Add Note".
  const saveNote = async () => {
    if (!order?.internalId || !newNote.trim()) return;
    setNoteSaving(true);
    try {
      await addOrderNote(order.internalId, newNote.trim(), me?.staffName, 1);
      const refreshed = await fetchOrderNotes(order.internalId, 1);
      setInternalNotes(Array.isArray(refreshed) ? refreshed : []);
      setNewNote('');
      flash('notes', true, 'Note saved.');
    } catch (e) { flash('notes', false, e.message); }
    finally { setNoteSaving(false); }
  };

  const pushEntry = (row) => {
    if (row) setDesk(d => ({ ...d, exists: true, entries: [...d.entries, row] }));
  };

  const saveCr = async () => {
    if (!crType || !crBody.trim()) return;
    setCrSaving(true);
    try {
      pushEntry(await addOrderDeskEntry({ orderId: order.id, kind: 'cr', entryType: crType, body: crBody.trim() }));
      setCrBody('');
      flash('cr', true, 'Entry saved.');
    } catch (e) { flash('cr', false, e.message); }
    finally { setCrSaving(false); }
  };

  const saveProblem = async () => {
    if (!problemBody.trim()) return;
    setProblemSaving(true);
    try {
      pushEntry(await addOrderDeskEntry({ orderId: order.id, kind: 'problem', body: problemBody.trim() }));
      setProblemBody('');
      flash('problems', true, 'Problem logged.');
    } catch (e) { flash('problems', false, e.message); }
    finally { setProblemSaving(false); }
  };

  const resolveProblem = async (id) => {
    const text = (resolveText[id] || '').trim();
    if (!text) return;
    setResolving(id);
    try {
      const row = await resolveOrderDeskProblem(id, text);
      setDesk(d => ({ ...d, entries: d.entries.map(e => (e.id === id ? row : e)) }));
      setResolveText(r => ({ ...r, [id]: '' }));
      flash('problems', true, 'Marked resolved.');
    } catch (e) { flash('problems', false, e.message); }
    finally { setResolving(null); }
  };

  const smsPhone = billing.phone || shipping.phone || '';
  const openSms = () => {
    const first = toTitleCase((billing.name || '').split(' ')[0]);
    setSmsBody(`Hi ${first}, it's ${me?.staffName || 'The Crab Place'} with CrabPlace.com and I'm contacting you about your order.`);
    setSmsResult(null);
    setShowSms(true);
    setTimeout(() => commsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };
  // Sends a REAL text (the classic page's Text Customer call), after a confirm. Then records it
  // on the order so it shows under Activity; a failed record never undoes the send.
  const sendSms = async () => {
    if (!smsPhone) { setSmsResult({ ok: false, msg: 'No phone number on this order.' }); return; }
    if (!window.confirm(`Send this text to ${fmtPhone(smsPhone)} now? It goes to the customer.`)) return;
    setSmsSending(true); setSmsResult(null);
    try {
      await sendCustomerSms(smsPhone, smsBody);
      setSmsResult({ ok: true, msg: 'Text sent.' });
      addOrderDeskEntry({ orderId: order.id, kind: 'contact', entryType: 'Text', body: `To ${fmtPhone(smsPhone)}: ${smsBody}` })
        .then(pushEntry)
        .catch(() => setSmsResult({ ok: true, msg: 'Text sent, but it could not be added to Activity.' }));
      setTimeout(() => setShowSms(false), 2500);
    } catch (e) { setSmsResult({ ok: false, msg: e.message }); }
    finally { setSmsSending(false); }
  };

  // Tabs: arrow keys move between them (WAI-ARIA tabs pattern).
  const onTabKey = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    let j = null;
    if (e.key === 'ArrowRight') j = (i + 1) % TABS.length;
    if (e.key === 'ArrowLeft') j = (i - 1 + TABS.length) % TABS.length;
    if (e.key === 'Home') j = 0;
    if (e.key === 'End') j = TABS.length - 1;
    if (j == null) return;
    e.preventDefault();
    setTab(TABS[j][0]);
    tabRefs.current[TABS[j][0]]?.focus();
  };

  const stampPreview = <span className="odx-stamp">Stamped automatically: <strong>{fmtDay(now)} · {fmtTime(now)} · {me?.staffName || 'you'}</strong></span>;
  const msgFor = (where) => (formMsg && formMsg.where === where
    ? <span className={formMsg.ok ? 'odx-ok' : 'odx-err'}>{formMsg.text}</span> : null);

  // ── No order / loading / error ─────────────────────────────────────────────
  if (!orderId || error) {
    return (
      <div className="odx">
        <div className="breadcrumb"><a onClick={() => navigateTo('dashboard')}>Dashboard</a><span>›</span><span>Order Desk (beta)</span></div>
        <div className="page-header">Order Desk <span className="odx-beta">beta</span></div>
        <div className="odx-card odx-empty-start">
          {error && notFound && <div className="odx-warn"><strong>Order #{orderId} was not found.</strong> Check the number and try again.</div>}
          {error && !notFound && <div className="odx-warn"><strong>Could not load order {orderId}:</strong> {error}</div>}
          <div className="odx-card-title">Open an order</div>
          <p className="odx-muted">Type the order number from BigCommerce or the confirmation email.</p>
          <OrderLookup navigateTo={navigateTo} />
        </div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="odx">
        <div className="breadcrumb"><a onClick={() => navigateTo('dashboard')}>Dashboard</a><span>›</span><span>Order Desk (beta)</span></div>
        <div className="odx-card odx-muted" style={{ textAlign: 'center' }}>Loading order {orderId}...</div>
      </div>
    );
  }

  const statusTone = { Completed: 'good', Shipped: 'good', Processing: 'info', 'On Hold': 'warn', Cancelled: 'bad' }[order.status] || 'info';
  const analysisKey = order.customer.username || (order.customer.id && order.customer.id !== '0' ? order.customer.id : '');
  const vipActive = vip?.vipColorStatus === 'active';
  const productSubtotal = order.products.reduce((s, p) => s + Number(String(p.total).replace(/[$,]/g, '')), 0);
  // ⚠️ transformOrderFromNeon has no line for orders.manual_discount (the VIP / staff discount),
  // so on 2027961 items + shipping came to $330.94 against a $309.26 total. Show it here, and
  // say so plainly if the lines still do not add up, rather than print a total nobody can check.
  const manualDiscount = Number(src?.manual_discount || 0);
  const linesSum = productSubtotal - manualDiscount
    + order.lineItems.reduce((s, li) => s + (Number(String(li.price).replace(/[$,]/g, '')) || 0), 0);
  const totalNum = Number(String(order.total).replace(/[$,]/g, ''));
  const unreconciled = Math.abs(linesSum - totalNum) > 0.01 ? totalNum - linesSum : 0;

  return (
    <div className="odx">
      <div className="breadcrumb">
        <a onClick={() => navigateTo('dashboard')}>Dashboard</a><span>›</span>
        <span>Order Desk (beta)</span><span>›</span><span>#{order.id}</span>
      </div>

      {/* ── Header ── */}
      <div className="odx-head">
        <div className="odx-head-main">
          <h1 className="odx-title">
            Order #{order.id}
            <span className={`odx-pill odx-pill-${statusTone}`}>{order.status}</span>
          </h1>
          <div className="odx-sub">
            {placed ? `Placed ${fmtDay(placed)} at ${fmtTime(placed)}` : `Placed ${order.orderDate}`}
            <span className="odx-dot">·</span>Customer #{order.customer.id || '—'}
            {order.internalId && order.internalId !== order.id && <><span className="odx-dot">·</span>Admin #{order.internalId}</>}
          </div>
        </div>
        <div className="odx-actions">
          <OrderLookup navigateTo={navigateTo} compact />
          <button className="btn btn-outline" onClick={() => navigateTo('order-details', order.id)} title="Open this order on the classic Order Details page (edits, holds, claims, packing invoice)">Classic view</button>
          <button className="btn btn-primary" onClick={openSms}><MessageSquare size={13} /> Contact customer</button>
        </div>
      </div>

      <div className="odx-banner">
        <strong>Order Desk (beta), live data.</strong> Notes, customer relations entries and problems saved here are real and stamped with your name. Edits to the order itself are still on the classic view.
      </div>

      <div className="odx-grid">
        {/* ═══════════ MAIN COLUMN ═══════════ */}
        <div className="odx-main">

          {/* Delivery & fulfillment */}
          <section className="odx-card" aria-labelledby="odx-delivery">
            <div className="odx-card-head">
              <h2 id="odx-delivery" className="odx-card-title">Delivery &amp; fulfillment</h2>
              <span className="odx-eyebrow">{order.shipMethod || 'Ship method not recorded'}</span>
            </div>
            <div className="odx-dates">
              <div>
                <Label>Ship date</Label>
                <div className="odx-big">{shipDay ? fmtWeekday(shipDay) : (order.shipDate || 'Not set')}</div>
                <div className="odx-small">{shipDay ? shipDay.getFullYear() : ''}</div>
              </div>
              <div>
                <Label>Requested delivery</Label>
                <div className="odx-big odx-orange">{requested ? fmtWeekday(requested) : (order.selectedDelivery || 'Not chosen')}</div>
                <div className="odx-small">{requested ? `${requested.getFullYear()} · Customer selected` : ''}</div>
              </div>
              <div>
                <Label>{delivered || upsDelivered ? 'Delivered' : 'Carrier estimate'}</Label>
                <div className="odx-big">
                  {(delivered || upsDelivered) ? fmtWeekday(upsDelivered || delivered)
                    : upsEta ? fmtWeekday(upsEta)
                    : estimated ? fmtWeekday(estimated)
                    : 'Not available'}
                </div>
                <div className="odx-small">
                  {upsDelivered ? `${fmtTime(upsDelivered) !== '12:00 AM' ? fmtTime(upsDelivered) + ' · ' : ''}UPS delivery scan`
                    : delivered ? 'From the order record'
                    : upsEta ? 'UPS scheduled delivery'
                    : estimated ? 'Estimated at checkout'
                    : upsState === 'loading' ? 'Checking UPS...' : ''}
                </div>
              </div>
            </div>
            <div className="odx-track">
              {order.trackingNumber ? (
                <>
                  <div>
                    <div className="odx-track-line"><span className={`odx-dotmark ${ups?.delivered || ups?.statusCode === '011' ? 'odx-dot-good' : ''}`} />{upsStatus || (upsState === 'loading' ? 'Checking UPS...' : upsState === 'error' ? 'UPS did not answer' : 'Tracking on file')}</div>
                    <div className="odx-small">
                      {order.trackingNumber}
                      {ups?.service ? ` · ${ups.service}` : ''}
                      {ups?.weight ? ` · ${ups.weight}` : ''}
                    </div>
                  </div>
                  <div className="odx-actions">
                    {ups?.podLetterHtml && (
                      // Same as the classic UPS Details panel: the letter UPS returns, in a new window.
                      <button className="btn btn-outline" onClick={() => { const w = window.open('', '_blank'); if (w) { w.document.write(ups.podLetterHtml); w.document.close(); } }}>Proof of delivery</button>
                    )}
                    <a className="btn btn-outline" href={`https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(order.trackingNumber)}`} target="_blank" rel="noopener noreferrer">Track on UPS <ExternalLink size={12} /></a>
                  </div>
                </>
              ) : (
                <div><div className="odx-track-line"><span className="odx-dotmark" />No tracking number yet</div><div className="odx-small">It appears once the label is printed.</div></div>
              )}
            </div>
            <div className="odx-facts">
              <span><b>Packer</b> {order.packerName ? toTitleCase(order.packerName) : '—'}</span>
              <span><b>Box</b> {order.boxType || '—'}</span>
              <span><b>Gel packs</b> {order.gelPacks || '—'}</span>
              <span><b>Dry ice</b> {order.dryIce || '—'}</span>
              <span><b>Crab steamed</b> {order.crabSteamedDate ? fmtDay(when(order.crabSteamedDate)) : '—'}</span>
              <span><b>Box weight</b> {order.boxWeight > 0 ? `${order.boxWeight} lbs` : ups?.charges?.billedWeight ? `${ups.charges.billedWeight} (UPS)` : '—'}</span>
            </div>
          </section>

          {/* Addresses */}
          <section className="odx-card" aria-labelledby="odx-addresses">
            <div className="odx-card-head">
              <h2 id="odx-addresses" className="odx-card-title">Addresses</h2>
              <button className="odx-link" onClick={() => setShowAddresses(true)}>Previous shipping addresses{addresses ? ` (${addresses.length})` : ''}</button>
            </div>
            <div className="odx-two">
              <div>
                <Label>Ship to · recipient</Label>
                <div className="odx-strong">{toTitleCase(shipping.name)}</div>
                <div>{toTitleCase(shipping.address)}</div>
                <div>{toTitleCase(shipping.city)}{shipping.city ? ', ' : ''}{shipping.state} {shipping.zip}</div>
                {shipping.phone && <div>{fmtPhone(shipping.phone)}</div>}
                {shipping.residential && <div className="odx-small">Residential</div>}
              </div>
              <div>
                <Label>Bill to · customer</Label>
                <div className="odx-strong">{toTitleCase(billing.name)}</div>
                <div>{toTitleCase(billing.address)}</div>
                <div>{toTitleCase(billing.city)}{billing.city ? ', ' : ''}{billing.state} {billing.zip}</div>
                {billing.phone && <div>{fmtPhone(billing.phone)}</div>}
                {billing.email && <div className="odx-break">{billing.email}</div>}
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section className="odx-card odx-tabcard">
            <div className="odx-tabs" role="tablist" aria-label="Order information">
              {TABS.map(([k, label]) => (
                <button
                  key={k}
                  ref={el => { tabRefs.current[k] = el; }}
                  role="tab"
                  id={`odx-tab-${k}`}
                  aria-selected={tab === k}
                  aria-controls={`odx-panel-${k}`}
                  tabIndex={tab === k ? 0 : -1}
                  className={tab === k ? 'odx-tab odx-tab-on' : 'odx-tab'}
                  onClick={() => setTab(k)}
                  onKeyDown={onTabKey}
                >
                  {label}
                  {k === 'cr' && crEntries.length > 0 && <span className="odx-count">{crEntries.length}</span>}
                  {k === 'history' && histOrders.length > 0 && <span className="odx-count odx-count-navy">{orderCountText}</span>}
                </button>
              ))}
            </div>
            <div className="odx-panel" role="tabpanel" id={`odx-panel-${tab}`} aria-labelledby={`odx-tab-${tab}`}>

              {tab === 'items' && (
                <>
                  <div className="odx-scroll">
                    <table className="odx-table">
                      <thead><tr><th>Product</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Total</th></tr></thead>
                      <tbody>
                        {order.products.map((p, i) => (
                          <tr key={`${p.id}-${i}`}>
                            <td>
                              <div className="odx-prod">
                                {p.image ? <img src={p.image} alt="" /> : <div className="odx-prod-ph" aria-hidden="true">🦀</div>}
                                <div>
                                  <div className="odx-strong">{plainName(p.name)}</div>
                                  {/* Option names arrive as "Steaming Options:" - drop the trailing colon. */}
                                  {Object.entries(p.options || {}).map(([k, v]) => <div key={k} className="odx-small">{k.replace(/:\s*$/, '')}: {v}</div>)}
                                </div>
                              </div>
                            </td>
                            <td className="num">{p.qty}</td>
                            <td className="num">{p.price}</td>
                            <td className="num">{p.total}</td>
                          </tr>
                        ))}
                        {order.products.length === 0 && <tr><td colSpan={4} className="odx-muted">No items on this order.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="odx-total-line"><span>Order total</span><strong>{order.total}</strong></div>
                  <div className="odx-small">Prices, charges and the total are on the Payment tab.</div>
                </>
              )}

              {tab === 'payment' && (
                <>
                  <table className="odx-sums">
                    <tbody>
                      <tr><td>Items</td><td className="num">{money(productSubtotal)}</td></tr>
                      {/* transformOrderFromNeon gives "$-5.04" or "-$5.04"; show negatives as ($5.04).
                          Zero charges are left out, except the shipping line. */}
                      {order.lineItems.map((li, i) => {
                        const n = Number(String(li.price).replace(/[$,]/g, ''));
                        if (!n && li.label !== order.shipMethod) return null;
                        return (
                          <tr key={i}>
                            <td>{li.label.replace(/\s*\(\)$/, '')}</td>
                            <td className={`num ${n < 0 ? 'odx-neg' : ''}`}>{n < 0 ? `(${money(-n)})` : money(n)}</td>
                          </tr>
                        );
                      })}
                      {manualDiscount > 0 && <tr><td>Discount</td><td className="num odx-neg">({money(manualDiscount)})</td></tr>}
                      <tr className="odx-sum-total"><td>Order total</td><td className="num">{order.total}</td></tr>
                      {order.balance != null && order.balance !== '' && <tr><td>Balance</td><td className="num">{money(order.balance)}</td></tr>}
                    </tbody>
                  </table>
                  {order.giftCertificate && <div className="odx-small">Gift certificate {order.giftCertificate.code}: {order.giftCertificate.amount}</div>}
                  {unreconciled !== 0 && (
                    <div className="odx-warn" style={{ marginTop: 12 }}>
                      These lines come to {money(linesSum)}, {unreconciled < 0 ? `${money(-unreconciled)} more` : `${money(unreconciled)} less`} than the order total. A charge or discount on this order is not in the CPx copy; check BigCommerce before quoting a number.
                    </div>
                  )}
                  <div className="odx-note-box">
                    Card transactions (charge, refunds, AVS) are not in the CPx copy of the order yet.
                    See them on the <button className="odx-link" onClick={() => navigateTo('order-details', order.id)}>classic view</button> or{' '}
                    <a className="odx-link" href={`${BC_CONTROL_PANEL}/manage/orders/${order.id}`} target="_blank" rel="noopener noreferrer">in BigCommerce <ExternalLink size={11} /></a>.
                  </div>
                </>
              )}

              {tab === 'activity' && (
                <>
                  <div className="odx-card-head">
                    <h3 className="odx-card-title">Order activity</h3>
                    <span className="odx-eyebrow">Newest last</span>
                  </div>
                  <p className="odx-muted">The order's dates, internal and system notes, UPS scans, customer relations entries, problems, and texts sent from this page.</p>
                  {activity.length === 0 ? <div className="odx-muted">Nothing recorded yet.</div> : (
                    <ol className="odx-timeline">
                      {activity.map((a, i) => (
                        <li key={i} className={`odx-tl odx-tl-${a.tone || 'base'}`}>
                          <div className="odx-strong">{a.title}</div>
                          <div className="odx-small">
                            {fmtStamp(a.at)}{a.who ? <> · <b>{a.who}</b></> : null}{a.detail ? ` · ${a.detail}` : ''}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </>
              )}

              {tab === 'history' && (
                <>
                  <div className="odx-card-head">
                    <h3 className="odx-card-title">Customer's orders</h3>
                    <span className="odx-eyebrow">{orderCount == null ? '' : `${orderCountText} order${orderCount === 1 ? '' : 's'}`}</span>
                  </div>
                  {!history ? <div className="odx-muted">{order.customer.id && order.customer.id !== '0' ? 'Loading...' : 'Guest checkout: no order history.'}</div> : (
                    <div className="odx-scroll">
                      <table className="odx-table odx-table-click">
                        <thead><tr><th>Order</th><th>Date</th><th>Ship method</th><th className="num">Amount</th></tr></thead>
                        <tbody>
                          {histOrders.map(h => {
                            const here = String(h.id) === String(order.id);
                            return (
                              <tr key={h.internalId || h.id} className={here ? 'odx-row-here' : ''}
                                onClick={() => { if (!here) navigateTo('order-desk', String(h.id)); }}
                                title={here ? 'This order' : 'Open in Order Desk'}>
                                <td className="odx-strong">#{h.id}{here && <span className="odx-pill odx-pill-info odx-pill-sm">This order</span>}</td>
                                <td>{fmtDay(when(h.date)) || '—'}</td>
                                <td>{h.shipMethod || '—'}</td>
                                <td className="num">{money(h.amount)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {orderCount >= 50 && <div className="odx-small">Showing the 50 most recent orders.</div>}
                    </div>
                  )}
                </>
              )}

              {tab === 'cr' && (
                <>
                  <div className="odx-card-head">
                    <h3 className="odx-card-title">Customer relations log</h3>
                  </div>
                  <p className="odx-muted">Upgrades, substitutions, credits, refunds, reships and courtesy calls on this order. Each entry is stamped with the date, time and your name.</p>
                  {desk.loading ? <div className="odx-muted">Loading...</div>
                    : desk.error ? <div className="odx-warn">Could not load the log: {desk.error}</div>
                    : crEntries.length === 0 ? <div className="odx-empty">Nothing logged on this order yet.</div>
                    : (
                      <div className="odx-crlist">
                        {crEntries.map(e => (
                          <div key={e.id} className="odx-cr">
                            <span className={`odx-tag odx-tag-${String(e.entry_type).toLowerCase().replace(/\s+/g, '-')}`}>{e.entry_type}</span>
                            <div>
                              <div className="odx-pre">{e.body}</div>
                              <div className="odx-small">{fmtStamp(when(e.created_at))} · <b>{e.created_by}</b></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  <div className="odx-form">
                    <div className="odx-form-row">
                      <label className="odx-field odx-field-type">
                        <span>Type</span>
                        <select className="form-select" value={crType} onChange={e => setCrType(e.target.value)}>
                          <option value="">Choose...</option>
                          {(desk.crTypes.length ? desk.crTypes : ['Upgrade', 'Substitution', 'Credit', 'Refund', 'Reship', 'Courtesy call', 'Other']).map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                      <label className="odx-field">
                        <span>What was done</span>
                        <input className="form-input" value={crBody} maxLength={2000} onChange={e => setCrBody(e.target.value)}
                          placeholder="e.g. Upgraded Large to Jumbo at no charge, Large sold out" onKeyDown={e => { if (e.key === 'Enter') saveCr(); }} />
                      </label>
                    </div>
                    <div className="odx-form-foot">
                      {stampPreview}
                      {msgFor('cr')}
                      <button className="btn btn-outline" disabled={crSaving || !crType || !crBody.trim()} onClick={saveCr}>{crSaving ? 'Saving...' : 'Add entry'}</button>
                    </div>
                  </div>
                </>
              )}

              {tab === 'details' && (
                <dl className="odx-dl">
                  <dt>Taken by</dt>
                  <dd>
                    {takenBy || order.takenBy
                      ? <><strong>{takenBy || order.takenBy}</strong> <span className="odx-muted">(placed for the customer)</span></>
                      : <><strong>Online</strong> <span className="odx-muted">(website checkout)</span></>}
                  </dd>
                  <dt>Order placed</dt><dd>{placed ? `${fmtDay(placed)} · ${fmtTime(placed)}` : order.orderDate}</dd>
                  <dt>Order type</dt><dd>{isGift ? 'Gift order (recipient differs from buyer)' : 'Ships to the buyer'}</dd>
                  <dt>Ship method</dt><dd>{order.shipMethod || <span className="odx-muted">None</span>}</dd>
                  <dt>Source / referrer</dt>
                  <dd>
                    {attribution && (attribution.utm_source || attribution.utm_medium || attribution.utm_campaign)
                      ? [attribution.utm_source, attribution.utm_medium, attribution.utm_campaign].filter(Boolean).join(' · ')
                      : order.orderReferredBy || <span className="odx-muted">Not recorded</span>}
                    {attribution?.fbclid && <span className="odx-pill odx-pill-info odx-pill-sm">Facebook click</span>}
                    {attribution?.gclid && <span className="odx-pill odx-pill-info odx-pill-sm">Google click</span>}
                  </dd>
                  <dt>Coupon</dt>
                  <dd>{src?.coupon ? `${src.coupon}${order.couponDiscount ? ` (${money(order.couponDiscount)} off)` : ''}` : order.couponDiscount ? money(order.couponDiscount) : <span className="odx-muted">None used</span>}</dd>
                  <dt>Gift message</dt><dd className="odx-pre">{order.giftMessage || <span className="odx-muted">None</span>}</dd>
                  <dt>Packing notes</dt><dd className="odx-pre">{order.packingNotes || <span className="odx-muted">None</span>}</dd>
                  <dt>Shipping instructions</dt><dd className="odx-pre">{order.shippingInstructions || <span className="odx-muted">None</span>}</dd>
                  <dt>Declared value</dt><dd>{money(order.declaredValue)}</dd>
                  <dt>Insurance cost</dt><dd>{money(order.insuranceCost)}</dd>
                  <dt>Order profit</dt><dd>{order.orderProfit < 0 ? `(${money(Math.abs(order.orderProfit))})` : money(order.orderProfit)}</dd>
                  {order.scaleShipCost > 0 && <><dt>Scale ship cost</dt><dd>{money(order.scaleShipCost)}</dd></>}
                  {order.claimNumber && <><dt>Claim number</dt><dd>{order.claimNumber}</dd></>}
                  <dt>Flags</dt>
                  <dd>
                    {[['Fraud', order.isFraud], ['Crab event', order.crabEvent], ['Claim order', order.claimOrder], ['Gift card(s) sent', order.physicalGiftCardSent], ['Product alert', order.productAlert], ['Saturday delivery', order.saturdayDelivery]]
                      .filter(([, on]) => on).map(([l]) => <span key={l} className="odx-pill odx-pill-warn odx-pill-sm">{l}</span>)}
                    {![order.isFraud, order.crabEvent, order.claimOrder, order.physicalGiftCardSent, order.productAlert, order.saturdayDelivery].some(Boolean) && <span className="odx-muted">None</span>}
                  </dd>
                  <dt>Fraud score</dt><dd>{order.fraudScore ?? 0}</dd>
                </dl>
              )}
            </div>
          </section>

          {/* Internal notes | Problems & resolutions */}
          <section className="odx-card">
            <div className="odx-card-head">
              <div className="odx-toggle" role="tablist" aria-label="Staff notes">
                <button role="tab" aria-selected={notesView === 'notes'} className={notesView === 'notes' ? 'on' : ''} onClick={() => setNotesView('notes')}>
                  Internal notes{internalNotes.length > 0 && <span className="odx-count odx-count-navy">{internalNotes.length}</span>}
                </button>
                <button role="tab" aria-selected={notesView === 'problems'} className={notesView === 'problems' ? 'on' : ''} onClick={() => setNotesView('problems')}>
                  Problems &amp; resolutions{openProblems > 0 && <span className="odx-count">{openProblems}</span>}
                </button>
              </div>
              <span className="odx-eyebrow">Staff only</span>
            </div>

            {notesView === 'notes' && (
              <>
                <p className="odx-muted">The same internal notes as the classic Order Details and the old admin.</p>
                {internalNotes.length === 0 ? <div className="odx-empty">No internal notes on this order.</div> : (
                  <div className="odx-notes">
                    {internalNotes.map((n, i) => (
                      <div key={i} className="odx-noteitem">
                        <div className="odx-pre">{n.description}</div>
                        {n.response && <div className="odx-small">Response: {n.response}</div>}
                        <div className="odx-small">{fmtStamp(when(n.date)) || 'No date'} · <b>{n.employee || 'Unknown'}</b></div>
                      </div>
                    ))}
                  </div>
                )}
                <textarea className="form-textarea odx-textarea" placeholder="Add an internal note..." value={newNote} onChange={e => setNewNote(e.target.value)} />
                <div className="odx-form-foot">
                  {stampPreview}
                  {msgFor('notes')}
                  <button className="btn btn-outline" disabled={noteSaving || !newNote.trim() || !order.internalId} onClick={saveNote}>{noteSaving ? 'Saving...' : 'Add note'}</button>
                </div>
              </>
            )}

            {notesView === 'problems' && (
              <>
                <p className="odx-muted">Each problem has a status, the problem, the resolution, and who and when for both. Open problems stay at the top.</p>
                {desk.loading ? <div className="odx-muted">Loading...</div>
                  : desk.error ? <div className="odx-warn">Could not load problems: {desk.error}</div>
                  : problems.length === 0 ? <div className="odx-empty">No problems logged on this order.</div>
                  : problems.map(p => (
                    <div key={p.id} className={`odx-problem ${p.status === 'open' ? 'odx-problem-open' : 'odx-problem-done'}`}>
                      <span className={`odx-pill ${p.status === 'open' ? 'odx-pill-bad' : 'odx-pill-good'} odx-pill-sm`}>{p.status === 'open' ? 'Open' : 'Resolved'}</span>
                      <Label>Problem</Label>
                      <div className="odx-pre">{p.body}</div>
                      <div className="odx-small">Logged {fmtStamp(when(p.created_at))} · <b>{p.created_by}</b></div>
                      <Label>Resolution</Label>
                      {p.status === 'open' ? (
                        <>
                          <div className="odx-muted">Not resolved yet.</div>
                          <textarea className="form-textarea odx-textarea" placeholder="What was done to resolve it..." value={resolveText[p.id] || ''}
                            onChange={e => setResolveText(r => ({ ...r, [p.id]: e.target.value }))} />
                          <div className="odx-form-foot">
                            <span />
                            <button className="btn btn-outline" disabled={resolving === p.id || !(resolveText[p.id] || '').trim()} onClick={() => resolveProblem(p.id)}>
                              {resolving === p.id ? 'Saving...' : 'Mark resolved'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="odx-pre">{p.resolution}</div>
                          <div className="odx-small">Resolved {fmtStamp(when(p.resolved_at))} · <b>{p.resolved_by}</b></div>
                        </>
                      )}
                    </div>
                  ))}
                <textarea className="form-textarea odx-textarea" placeholder="Describe the problem (what the customer reported, what went wrong)..." value={problemBody} maxLength={2000} onChange={e => setProblemBody(e.target.value)} />
                <div className="odx-form-foot">
                  {stampPreview}
                  {msgFor('problems')}
                  <button className="btn btn-outline" disabled={problemSaving || !problemBody.trim()} onClick={saveProblem}>{problemSaving ? 'Saving...' : 'Log problem'}</button>
                </div>
              </>
            )}
          </section>
        </div>

        {/* ═══════════ SIDE COLUMN ═══════════ */}
        <aside className="odx-side">
          <section className="odx-card">
            <div className="odx-card-head">
              <h2 className="odx-card-title">Customer</h2>
              {vipActive && <span className="odx-pill odx-pill-vip">★ {vip.vipNotRenewing ? 'VIP · not renewing' : 'VIP member'}</span>}
            </div>
            <div className="odx-cust">
              <div className="odx-avatar" aria-hidden="true">{initials(customerName)}</div>
              <div>
                <div className="odx-strong odx-cust-name">{customerName || 'Unknown'}</div>
                <div className="odx-small">{vip?.joinedYear ? `VIP member since ${vip.joinedYear}` : `Customer #${order.customer.id || '—'}`}</div>
              </div>
            </div>
            <div className="odx-stats">
              <div><div className="odx-stat">{orderCountText}</div><Label>Total orders</Label></div>
              <div><div className="odx-stat">{history?.summary ? money(history.summary.averageOrderAmount) : '—'}</div><Label>Avg. order</Label></div>
            </div>
            <div className="odx-contact">
              {order.customer.username && <div className="odx-break">{order.customer.username}</div>}
              {smsPhone && <div>{fmtPhone(smsPhone)}</div>}
            </div>
            <div className="odx-stack">
              {analysisKey && (
                <a className="btn btn-outline" href={`/?page=customer-analysis&q=${encodeURIComponent(analysisKey)}`} target="_blank" rel="noopener noreferrer">
                  Customer analysis <ExternalLink size={12} />
                </a>
              )}
              <button className="btn btn-outline" onClick={() => setShowAddresses(true)}>
                <MapPin size={12} /> Previous shipping addresses{addresses ? ` (${addresses.length})` : ''}
              </button>
            </div>
          </section>

          <section className="odx-card">
            <div className="odx-card-head"><h2 className="odx-card-title">VIP benefits</h2>{vipActive && <span className="odx-star" aria-hidden="true">★</span>}</div>
            <div className="odx-kv">
              <span>Membership</span>
              <strong>{!vip ? '...' : vipActive ? (vip.vipNotRenewing ? 'Active, not renewing' : 'Active') : vip.vipColorStatus === 'expired' ? 'Expired' : 'Not a member'}</strong>
            </div>
            <div className="odx-kv">
              <span>Available rewards</span>
              {bcCustomerId
                ? <button className="odx-link odx-strong" onClick={() => setRewardsOpen(true)} title="Reward history">{storeCredit == null ? '...' : money(storeCredit)}</button>
                : <strong>{storeCredit == null ? '—' : money(storeCredit)}</strong>}
            </div>
            <div className="odx-kv"><span>First order</span><strong>{fmtDay(when(history?.summary?.firstOrderDate)) || '—'}</strong></div>
            <div className="odx-kv"><span>Last order</span><strong>{fmtDay(when(history?.summary?.lastOrderDate)) || '—'}</strong></div>
          </section>

          <section className="odx-card" ref={commsRef}>
            <div className="odx-card-head"><h2 className="odx-card-title">Communication</h2></div>
            <ListStatus contactIds={order.customer.id && order.customer.id !== '0' ? [order.customer.id] : []} email={order.customer.username} style={{ marginBottom: 10 }} />
            {!showSms ? (
              <button className="btn btn-outline odx-full" onClick={openSms} disabled={!smsPhone} title={smsPhone ? '' : 'No phone number on this order'}>
                <MessageSquare size={12} /> Text customer
              </button>
            ) : (
              <div className="odx-sms">
                <div className="odx-card-head">
                  <span className="odx-strong">Text {fmtPhone(smsPhone)}</span>
                  <button className="odx-icon" onClick={() => setShowSms(false)} aria-label="Close"><X size={14} /></button>
                </div>
                <textarea className="form-textarea odx-textarea" rows={4} value={smsBody} onChange={e => setSmsBody(e.target.value)} />
                <div className="odx-small">This sends a real text, the same as Text Customer on the classic view. It is added to Activity with your name.</div>
                <div className="odx-form-foot">
                  {smsResult ? <span className={smsResult.ok ? 'odx-ok' : 'odx-err'}>{smsResult.msg}</span> : <span />}
                  <button className="btn btn-primary" disabled={smsSending || !smsBody.trim()} onClick={sendSms}><Send size={12} /> {smsSending ? 'Sending...' : 'Send text'}</button>
                </div>
              </div>
            )}
            <div className="odx-small odx-coming">Email from this page: coming next.</div>
          </section>

          {isGift && (
            <section className="odx-card odx-context">
              <div className="odx-strong">Service context</div>
              <div className="odx-small">The recipient ({toTitleCase(shipping.name)}) is not the buyer ({toTitleCase(billing.name)}). Confirm who you are talking to before discussing delivery.</div>
            </section>
          )}
        </aside>
      </div>

      {/* Previous shipping addresses */}
      {showAddresses && (
        <div className="odx-modal-bg" onClick={() => setShowAddresses(false)}>
          <div className="odx-modal" role="dialog" aria-modal="true" aria-labelledby="odx-addr-title" onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === 'Escape') setShowAddresses(false); }}>
            <div className="odx-card-head">
              <h2 id="odx-addr-title" className="odx-card-title">Previous shipping addresses</h2>
              <button className="odx-icon" onClick={() => setShowAddresses(false)} aria-label="Close" autoFocus><X size={16} /></button>
            </div>
            <div className="odx-small">{customerName} · Customer #{order.customer.id}</div>
            {addresses == null ? <div className="odx-muted">Loading...</div>
              : addresses.length === 0 ? <div className="odx-empty">{order.customer.id === '0' ? 'Guest checkout: no saved history.' : 'No shipping addresses found.'}</div>
              : (
                <div className="odx-addrs">
                  {addresses.map((a, i) => {
                    const here = addrKey(a.address1, a.zip) === currentKey;
                    return (
                      <div key={i} className={here ? 'odx-addr odx-addr-here' : 'odx-addr'}>
                        <div>
                          <div className="odx-strong">{toTitleCase(a.name || '')}</div>
                          <div>{toTitleCase(a.address1)}{a.address2 ? `, ${a.address2}` : ''}</div>
                          <div>{toTitleCase((a.city || '').trim())}, {a.state} {a.zip}</div>
                          <div className="odx-small">
                            Last used {fmtDay(when(a.last_used))} · {a.orders} order{a.orders === 1 ? '' : 's'} shipped here
                            {a.linked ? ' · includes a linked account' : ''}
                          </div>
                        </div>
                        {here && <span className="odx-pill odx-pill-good odx-pill-sm">On this order</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            <div className="odx-small">Grouped by street and ZIP; spelling and punctuation differences are merged. Cancelled orders are not counted. To change this order's ship-to, use the classic view.</div>
          </div>
        </div>
      )}

      <VipRewardsModal
        open={rewardsOpen}
        onClose={() => setRewardsOpen(false)}
        bcCustomerId={bcCustomerId}
        customerName={customerName}
        storeCredit={storeCredit}
        showDrift={true}
      />
    </div>
  );
}
